import { Stack } from '@mui/material';
import { Match, Switch } from '@side/pantry';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import { InteractionType } from '@/common/types/interaction';
import { CancelButton } from '@/views/NewInteraction/CancelButton';
import { TrackOfferEvent } from '$analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from '$analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import { useCreateOfferContext } from '../context/CreateOfferContext';
import { BuyerMethod, type CreateOfferFormData } from '../types';
import { BuyerTypeSection } from './BuyerTypeSection';
import { ExistingBuyerCardSections } from './ExistingBuyerCardSections';
import { MyListingSummary } from './MyListingSummary';
import { NewBuyerCardSections } from './NewBuyerCardSections';
import { OfferSubmitButton } from './OfferSubmitButton';
import { UnrepresentedBuyerDetails } from './UnrepresentedBuyerDetails';

const Footer = () => (
  <Stack
    sx={{
      gap: 2,
      flexDirection: { xs: 'column', lg: 'row' },
      justifyContent: { xs: 'center', lg: 'flex-end' },
    }}
  >
    <CancelButton type={InteractionType.offer} />
    <OfferSubmitButton />
  </Stack>
);

export const BuyerStep = () => {
  const buyerMethod = useWatch<CreateOfferFormData, 'buyerMethod'>({
    name: 'buyerMethod',
  });
  const { getValues } = useFormContext<CreateOfferFormData>();
  const { linkSource } = useCreateOfferContext();

  const trackOfferEvent = useTrackOfferAnalytics();

  useEffectOnce(() => {
    const listingId = getValues('listingId');
    trackOfferEvent(TrackOfferEvent.CreateOfferBuyerStepLanding, {
      listingId,
      // lom/modal are set explicitly; a listingId with neither was linked via
      // the autocomplete dropdown.
      linkSource: linkSource ?? (listingId ? 'dropdown' : undefined),
    });
  });

  return (
    <>
      <MyListingSummary />
      <BuyerTypeSection />
      <Switch>
        <Match when={buyerMethod === BuyerMethod.unrepresentedBuyer}>
          <UnrepresentedBuyerDetails />
        </Match>
        <Match when={buyerMethod === BuyerMethod.newBuyer}>
          <NewBuyerCardSections />
        </Match>
        <Match when={buyerMethod === BuyerMethod.existingBuyer}>
          <ExistingBuyerCardSections />
        </Match>
      </Switch>
      <Footer />
    </>
  );
};
